/* PortalBreadCrumbs auto-generated by npm run generator */
import { Breadcrumbs, Link, makeStyles, Theme, Typography } from "@material-ui/core"
import NavigateNextIcon from "@material-ui/icons/NavigateNext"
import { PortalRole } from "@qlarity/common"
import clsx from "clsx"
import { observable } from "mobx"
import { observer } from "mobx-react"
import React, { CSSProperties } from "react"
import { generatePath, Link as RouterLink, matchPath, useLocation } from "react-router-dom"
import { PATHS, ROUTE_PATHS } from "Router"
import Store from "store/Store"

interface PortalBreadCrumbsProps {
    className?: string
    style?: CSSProperties
    children?: React.ReactNode
}

class PortalBreadCrumbsState {
    @observable
    componentName = "PortalBreadCrumbs"
    @observable
    orgName?: string
    @observable
    clinicianName?: string
    @observable
    clientName?: string
}

const useStyles = makeStyles((theme: Theme) => ({
    portalBreadCrumbsMolecule: {
        margin: theme.spacing(2, 0, 4),
        "& a": {
            color: theme.palette.text.primary,
        },
    },
    disablePointer: {
        pointerEvents: "none",
        cursor: "default",
    },
}))

const PortalBreadCrumbs: React.FC<PortalBreadCrumbsProps> = (props) => {
    const classes = useStyles()
    const className = clsx(classes.portalBreadCrumbsMolecule, props.className)
    const state = React.useRef(new PortalBreadCrumbsState()).current
    const { pathname } = useLocation()
    const match = matchPath<{ orgId?: string; clinicianId?: string; clientCaseId?: string }>(pathname, {
        path: Object.values(ROUTE_PATHS),
        exact: true,
    })

    if (match?.params.orgId) {
        state.orgName = Store.auth.getOrg(match.params.orgId).orgName
    }
    React.useLayoutEffect(() => {
        if (match?.params.clinicianId) {
            Store.auth.clinicianCollection
                .doc(match?.params.clinicianId)
                .get()
                .then((clinicinian) => (state.clinicianName = clinicinian.data()?.displayName))
        }
        if (match?.params.clientCaseId) {
            Store.auth.clientCollection
                .doc(match?.params.clientCaseId)
                .get()
                .then((client) => (state.clientName = client.data()?.displayName))
        }
    }, [match, state.clientName, state.clinicianName, state.orgName])

    if (!Store.auth.isLoggedIn) {
        return null
    }

    return (
        <Breadcrumbs className={className} style={props.style} separator={<NavigateNextIcon />}>
            {Store.auth.portalUser?.role === PortalRole.ADMIN && (
                <Link
                    variant={match?.params.orgId ? "body2" : "subtitle2"}
                    underline={match?.params.orgId ? "hover" : "none"}
                    component={RouterLink}
                    to={PATHS.ADMIN()}
                >
                    Qlarity Admin
                </Link>
            )}
            {match?.params.orgId && state.orgName && (
                <Link
                    variant={match.params.clinicianId ? "body2" : "subtitle2"}
                    underline={match.params.clinicianId && Store.auth.portalUser?.role !== PortalRole.CLINICIAN ? "hover" : "none"}
                    component={RouterLink}
                    to={generatePath(ROUTE_PATHS.ORG, { ...match?.params })}
                    className={Store.auth.portalUser?.role === PortalRole.CLINICIAN ? classes.disablePointer : ""}
                >
                    {state.orgName}
                </Link>
            )}
            {match?.params.clinicianId && state.clinicianName && (
                <Link
                    variant={match.params.clientCaseId ? "body2" : "subtitle2"}
                    component={RouterLink}
                    to={generatePath(ROUTE_PATHS.CLINICIAN, { ...match?.params })}
                >
                    {state.clinicianName}
                </Link>
            )}
            {match?.params.clientCaseId && state.clientName && (
                <Link variant={"subtitle2"} component={RouterLink} to={generatePath(ROUTE_PATHS.CLIENT, { ...match?.params })} underline="none">
                    {state.clientName}
                </Link>
            )}
            {match?.path === ROUTE_PATHS.PROFILE && (
                <Link variant={"body2"} component={RouterLink} to={PATHS.HOME()}>
                    {Store.auth.authUser?.displayName}
                </Link>
            )}
            {match?.path === ROUTE_PATHS.PROFILE && (
                <Typography variant="subtitle2" color="textPrimary">
                    My Profile
                </Typography>
            )}
        </Breadcrumbs>
    )
}

export default observer(PortalBreadCrumbs)
